import {Injectable} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {WarehousingDataFormComponent} from "../../../store-management/warehousing/warehousing-data-form/warehousing-data-form.component";
import {Stock} from "../../../shared/view-model/stock";
import {StockIncrease} from "../../../store-management/model/warehousing";
import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {IUser} from "../../../shared/redux-store/session/session.types";
import {Supplier} from "../../../shared/view-model/crm/supplier.type";
import {WarehousingReasonInterface} from "../../../store-management/model/warehousing-reason-interface.type";
import {VisbosService} from "../../../core/visbos-core/graphql/visbos.service";
import {Apollo} from "apollo-angular";
import {NgRedux} from "@angular-redux/store";
import {IAppState} from "../../../shared/redux-store";
import {SessionActions} from "../../../shared/actions";
import {AbstractVisbosControl} from "../../../core/visbos-core/ui/forms/abstract-visbos-control";
import {
  CreateWarehousingResult,
  WarehousingService
} from "../../../store-management/services/warehousing-service/warehousing.service";
import {Observable} from "rxjs/Observable";

@Injectable()
export class ManualWarehousingService extends VisbosService {

  private increasedStocks: Array<StockIncrease> = [];
  increasedStocks$: BehaviorSubject<Array<StockIncrease>>;

  private hasAuthorizableArticles: boolean = false;
  private _isAuthorized: boolean = false;

  private _formControl: FormGroup;

  get formControl(): FormGroup {
    if (!this._formControl) {
      this._formControl = WarehousingDataFormComponent.initControl(this.fb);
    }

    return this._formControl;
  }

  getWarehousingReasonType(): string {
    return this.formControl.get('warehousing.warehousing_reason_type').value
  }

  getSupplierType(): string {
    let type = this.formControl.get('warehousing.supplier_type').value;

    return type;
  }

  getAuthUserName(): string {
    if (this.formControl.get('medicineAuthData.personal_number')) {
      return this.formControl.get('medicineAuthData.personal_number').value
    } else {
      return null;
    }
  }

  getWarehousingInputData() {
    return (<FormGroup>this.formControl.get('warehousing')).getRawValue();
  }

  getWarehousingAuthData() {
    if (this.formControl.get('medicineAuthData')) {
      return (<FormGroup>this.formControl.get('medicineAuthData')).getRawValue();
    } else {
      return null;
    }
  }

  getNoIncrreasedStocks(): number {
    return this.getIncreseadStocksFormArray().controls.length;
  }

  authorizedUser: IUser = null;
  warehousingRason: WarehousingReasonInterface = null;

  private _supplier: Supplier = null;

  set supplier(supplier: Supplier) {
    this._supplier = supplier;
    if (this.formControl.get('warehousing.supplier_cuid')) {
      this.formControl.get('warehousing.supplier_cuid').setValue(supplier.cuid);
    }
  }

  get supplier(): Supplier {
    return this._supplier;
  }

  authorizeWarehousing() {
    this._isAuthorized = this.formControl.get('medicineAuthData') && this.formControl.get('medicineAuthData').valid;
    this.authorizedUser = this.currentUser;
  }

  isAuthorized(): boolean {
    return this._isAuthorized && this.formControl.get('medicineAuthData') && this.formControl.get('medicineAuthData').valid;
  }

  needsAuthorization(): boolean {
    return this.hasAuthorizableArticles && !this.isAuthorized();
  }


  constructor(apollo: Apollo, ngRedux: NgRedux<IAppState>, sessionActions: SessionActions,
              private fb: FormBuilder, private whService: WarehousingService) {
    super(apollo, ngRedux, sessionActions);
    this.increasedStocks$ = new BehaviorSubject(this.increasedStocks);
  }

  private getIncreseadStocksFormArray(): FormArray {
    return this.formControl.get('warehousing.increased_stocks') as FormArray;
  }

  private initStockIncreaseForm(incSt?: StockIncrease): FormGroup {
    return this.fb.group({
      cuid: [AbstractVisbosControl.cuid()],
      stock_id: [incSt ? incSt.stock.cuid : null, Validators.required],
      amount: [incSt ? incSt.amount : null, Validators.required]
    })
  }

  private getStockIncreaseForm(stockCuid: string): FormGroup {
    for (let si of this.getIncreseadStocksFormArray().controls) {
      let val = si.get('stock_id').value;
      if (val == stockCuid) {
        return si as FormGroup;
      }
    }
    return null;
  }

  private getStockIncreaseFormIndex(stockCuid: string): number {
    for (let i = 0; i < this.getIncreseadStocksFormArray().length; i++) {
      let sf = this.getIncreseadStocksFormArray().at(i);
      let val = sf.get('stock_id').value;
      if (val == stockCuid) {
        return i;
      }
    }
    return -1;
  }

  addStock(stock: Stock) {
    let inc = this.increasedStocks.find((ic) => ic.stock.cuid == stock.cuid);

    if (inc) {
      this.updateStockAmount(stock, +inc.amount + 1);
    } else {
      let si: StockIncrease = {
        stock: stock,
        amount: 1
      };

      if (stock.registration_required) {
        this.hasAuthorizableArticles = true;
      }

      this.increasedStocks.unshift(si);
      this.getIncreseadStocksFormArray().push(this.initStockIncreaseForm(si));

    }
    this.onIncreaseStocksChanged();
  }

  updateStockAmount(stock: Stock, amount: number) {
    let st = this.increasedStocks.find((ic) => ic.stock.cuid == stock.cuid);
    let stForm = this.getStockIncreaseForm(stock.cuid);

    st.amount = +amount;
    stForm.get('amount').setValue(+amount);

    this.onIncreaseStocksChanged();
  }

  removeStock(stock: Stock) {
    let stIndex = this.increasedStocks.findIndex((ic) => ic.stock.cuid == stock.cuid);
    let stFormIndex = this.getStockIncreaseFormIndex(stock.cuid);

    if (stIndex >= 0) {
      this.increasedStocks.splice(stIndex,1);
      this.onIncreaseStocksChanged();
    }

    if (stFormIndex >= 0) {
      this.getIncreseadStocksFormArray().removeAt(stFormIndex);
    }

    // disable authorized if last auth stock is removed
    if (stock.registration_required) {
      if (!this.increasedStocks.find((st) => st.stock.registration_required)) {
        this.removeAuthData();
      }
    }

  }

  undoAddingStock(): Stock {
    let st = this.increasedStocks.pop().stock;
    this.onIncreaseStocksChanged();
    return st;
  }

  onIncreaseStocksChanged() {
    this.increasedStocks$.next(this.increasedStocks);
  }

  removeAuthData() {
    this.hasAuthorizableArticles = false;
    this._isAuthorized = false;
    this.authorizedUser = null;
    this.formControl.removeControl('medicineAuthData');
  }

  saveManualWarehhousing(): Observable<CreateWarehousingResult> {
    return this.whService.createWarehousing(this.getWarehousingInputData(), this.getWarehousingAuthData());
  }

}



// WEBPACK FOOTER //
// ./src/app/visbos-touch-client/ui/touch-warehousing/manual-warehousing.service.ts